import { z } from 'zod';
import {
  ElementIdSchema, ElementNodeSchema, FieldPathSchema, MAX_ELEMENT_DEPTH, countElements, elementDepth,
  type ElementNodeDto
} from './element.schema';
import { SharedCategoryEnum } from './theme-shared.schema';
import { ThemePageTypeEnum } from './theme.schema';
import {
  MAX_SETTING_DEPTH, MAX_SETTING_FIELDS, SettingFieldSchema, settingDepth, settingKeysUnique,
  type SettingFieldDto
} from './setting-field.schema';

// A block is a section somebody already built: a heading, a picture and three cards, arranged and
// styled, kept as the element tree it is. Dropping one mints an ordinary local subtree of the same
// basic elements every other part of the page is made of, so the merchant who drops it can take it
// apart, restyle any piece of it, or delete half of it. Nothing about it is a new kind of element.
//
// It has no renderer and no code: the body is ordinary elements, which is why a block can be added by
// writing a JSON file and why a page holding one costs a shopper nothing.
//
// What it does have is a PANEL. `settings` is this engine's `{% schema %}` — the controls a merchant
// gets when they select the band, declared beside the body instead of at the bottom of a Liquid file —
// and `targets` says which node and prop each of those controls writes. That is the whole reason the
// builder can open "Slide 1 image / Autoplay / Seconds" on a hero rather than the forty generic controls
// every `section` shares, without a hero ever becoming a kind of element.
//
// A block with no panel is still a block. It stamps and is forgotten, exactly as every one did before.
//
// They are the platform's, not a store's: shipped in the repo, read-only, and filed under the theme they
// were built for, so a store editing Argan sees Argan's blocks and no other theme's. The store-scoped
// library a merchant saves their own sections into is `theme-shared.schema.ts`, which answers a
// different question — that one is "mine", this one is "ours" — and shares this one's categories so the
// Add panel files both under the same headings.

/** The grammar a block id and a theme slug share: lowercase words joined by dashes. */
const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** A block's handle, in the same lowercase-dashed grammar a theme slug uses. */
export const BlockIdSchema = z.string().min(2).max(60).regex(SLUG_RE, 'A block id is lowercase words separated by dashes');

/** The picture on a block's card: a site-relative image path the admin serves from its public folder. */
const PREVIEW_RE = /^\/[A-Za-z0-9/_.-]+\.(?:webp|png|jpg)$/;

/** Where each control of a panel writes: its dotted path, to a node of the body and a path into its props. */
const PanelTargetsSchema = z.record(FieldPathSchema, z.object({
  nodeId: ElementIdSchema,
  path: FieldPathSchema,
  additionalNodeIds: z.array(ElementIdSchema).max(20).optional(),
  binding: z.literal('menu').optional()
}).strict());

/**
 * One kind of block a section takes inside it — Shopify's section `blocks` — such as a product page's
 * Title, Price or Buy buttons. The builder lists each one placed as a named part of its section, opens
 * the kind's own panel on it, and adds a fresh one from `node`. A placed one is recognised by its
 * template's id, carried as the node's own id or its `sourceId`, so no stored page needs a new field.
 */
export const ChildBlockSchema = z.object({
  /** The kind's handle, unique within its section. */
  type: BlockIdSchema,
  /** What Layers and Add block call it: "Title", "Price". */
  name: z.string().trim().min(1).max(60),
  /** The node of the section's body that holds this kind's placed blocks; a new one lands at its end. */
  parentId: ElementIdSchema,
  /** How many one section may hold; 1 for a part a page shows once. */
  limit: z.number().int().min(1).max(50).default(50),
  settings: z.array(SettingFieldSchema).max(MAX_SETTING_FIELDS).default([]),
  targets: PanelTargetsSchema.default({}),
  /** What a new one starts as. Its id is how every placed one is told apart from the section's own boxes. */
  node: ElementNodeSchema
}).strict();

/**
 * One block. The body's root must be a `section`, for two reasons that happen to agree: `ROOT_ACCEPTS`
 * is `['section']`, so that is the only thing a page's top level takes; and it lets the builder name a
 * dragged block's landing type from its id alone, without the card table having to be loaded first.
 */
export const BlockSchema = z.object({
  id: BlockIdSchema,
  /** The theme this block is part of. Its builder offers it, and so does any theme whose theme.json names it in `blocksFrom`. */
  theme: z.string().min(2).max(60).regex(SLUG_RE, 'A theme slug is lowercase words separated by dashes'),
  /** What the card calls it — "Header", never "Argan header": the theme is already the one being edited. */
  name: z.string().trim().min(1).max(80),
  /** One merchant-facing sentence, shown under the name on the card, in the element library's voice. */
  description: z.string().trim().min(1).max(200),
  category: SharedCategoryEnum,
  /** The block as it renders in its theme, so a merchant recognises it before they drop it. */
  preview: z.string().max(200).regex(PREVIEW_RE, 'A preview is a site-relative image path'),
  /** The panel this band opens, in the grammar every element already declares its controls in. */
  settings: z.array(SettingFieldSchema).max(MAX_SETTING_FIELDS).default([]),
  /**
   * Where each control writes: the control's own dotted path — `autoplay`, `slide1.heading` — to a node
   * in the body and a dotted path into its props. Keyed by the full path rather than the top-level key
   * because a group's rows rarely share a box: a slide's picture and its headline are two elements.
   */
  targets: PanelTargetsSchema.default({}),
  repeaters: z.array(z.object({
    nodeId: ElementIdSchema,
    label: z.string().trim().min(1).max(80),
    itemLabel: z.string().trim().min(1).max(60)
  }).strict()).max(10).default([]),
  /** The kinds of block this section takes inside it, which the builder lists and adds as named parts. */
  blocks: z.array(ChildBlockSchema).max(30).default([]),
  /** The pages the Add panel offers this section on; none means every page. */
  pages: z.array(ThemePageTypeEnum).max(20).default([]),
  node: ElementNodeSchema.refine((node) => node.type === 'section', 'A block is a section')
}).strict();

export type ChildBlockDto = z.infer<typeof ChildBlockSchema>;

/** How many elements one block may hold. A page allows 2000; a block is a band of it, not a page. */
export const MAX_BLOCK_NODES = 200;

/** What one block may weigh, serialised, for the same reason a tree has a ceiling: a prop is `unknown`. */
export const MAX_BLOCK_BYTES = 120_000;

export type BlockDto = z.infer<typeof BlockSchema>;

// Reports whether every id in one block's body is its own, which is what stops a drop colliding with itself.
function idsUnique(node: ElementNodeDto): boolean {
  const seen = new Set<string>();
  const walk = (current: ElementNodeDto): boolean =>
    !seen.has(current.id) && Boolean(seen.add(current.id)) && current.children.every(walk);
  return walk(node);
}

// Lists the dotted path of every control that holds a value, which is every one a target must name.
// A `group` holds rows rather than a value, so it is walked through and never aimed at itself.
function leafPaths(fields: readonly SettingFieldDto[], under = ''): string[] {
  return fields.flatMap((field) => {
    const path = under ? `${under}.${field.key}` : field.key;
    return field.control === 'group' && field.fields && field.fields.length > 0 ? leafPaths(field.fields, path) : [path];
  });
}

// Collects every id in one block's body, which is the set a panel's targets have to point into.
function bodyIds(node: ElementNodeDto, into: Set<string> = new Set()): Set<string> {
  into.add(node.id);
  node.children.forEach((child) => bodyIds(child, into));
  return into;
}

/**
 * Reports whether a block's panel actually drives its body.
 *
 * Three ways it can be a lie, and all three are silent at render: a control with no target writes
 * nowhere, a target with no control is dead weight nothing can reach, and a target naming a node the
 * body does not have throws a merchant's typing into a hole. A panel is checked once, at boot, against
 * the body beside it — which is the whole advantage of declaring one in a file the platform ships.
 */
function panelDrivesBody(settings: readonly SettingFieldDto[], targets: Record<string, { nodeId: string; additionalNodeIds?: string[] }>, node: ElementNodeDto): boolean {
  if (settingDepth(settings) > MAX_SETTING_DEPTH || !settingKeysUnique(settings)) return false;
  const written = new Set(leafPaths(settings));
  const aimed = Object.keys(targets);
  if (written.size !== aimed.length || !aimed.every((path) => written.has(path))) return false;
  const ids = bodyIds(node);
  return aimed.every((path) => [targets[path].nodeId, ...(targets[path].additionalNodeIds ?? [])].every((id) => ids.has(id)));
}

/**
 * Reads one block from a file's JSON, refusing one that is too big, too deep, or names a node twice.
 * `null` means the same thing it means for a page tree: this is not a block, and the caller says which
 * file it came from. A block is loaded once at boot, so a bad one is a startup warning, never a 500.
 */
export function readBlock(stored: unknown): BlockDto | null {
  const parsed = BlockSchema.safeParse(stored);
  if (!parsed.success) return null;
  const { node } = parsed.data;
  if (countElements([node]) > MAX_BLOCK_NODES) return null;
  // Measured after parsing, because a prop value is `unknown` and no per-field rule can bound one.
  if (JSON.stringify(parsed.data).length > MAX_BLOCK_BYTES) return null;
  if (elementDepth([node]) > MAX_ELEMENT_DEPTH) return null;
  if (!panelDrivesBody(parsed.data.settings, parsed.data.targets, node)) return null;
  const ids = bodyIds(node);
  if (parsed.data.repeaters.some((row) => !ids.has(row.nodeId))) return null;
  if (!childrenSound(parsed.data.blocks, ids)) return null;
  return idsUnique(node) ? parsed.data : null;
}

// Reports whether a section's block kinds hold together: each lands in a node of the body, drives its own
// template, and can be told apart from every other kind by its handle and by its template's id.
function childrenSound(kinds: readonly ChildBlockDto[], ids: ReadonlySet<string>): boolean {
  const types = new Set(kinds.map((kind) => kind.type));
  const templates = new Set(kinds.map((kind) => kind.node.id));
  if (types.size !== kinds.length || templates.size !== kinds.length) return false;
  return kinds.every((kind) => ids.has(kind.parentId) && idsUnique(kind.node)
    && countElements([kind.node]) <= MAX_BLOCK_NODES && panelDrivesBody(kind.settings, kind.targets, kind.node));
}

// Reports whether the Add panel offers a section on a page: every page, unless its file names some.
export const offeredOn = (block: Pick<BlockDto, 'pages'>, page: z.infer<typeof ThemePageTypeEnum>): boolean =>
  block.pages.length === 0 || block.pages.includes(page);
